/**
 * Articles list — pulls from /api/sp-articles (imported from
 * socialpulse.com Ghost CMS) on SocialPulse, or /api/articles
 * (the IFT-native CMS collection) when the page is served on
 * IndianFamilyTree.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Newspaper, Search, RefreshCw, ArrowUpRight, Plus } from "lucide-react";
import { resolveSiteSlug } from "@/config/siteConfig";

export default function ArticlesList() {
  const { user } = useAuth();
  const isAdmin = !!(user && (user.is_admin || user.is_super_admin));
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [tags, setTags] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [activeTag, setActiveTag] = useState(params.get("tag") || "");
  const [syncing, setSyncing] = useState(false);

  // IFT-native articles live in /api/articles with a different schema
  // (hero_image, tags, no excerpt) — everything else uses the imported
  // SocialPulse corpus via /api/sp-articles.
  const site = useMemo(() => resolveSiteSlug(), []);
  const isIft = site === "indianfamilytree";
  const brand = isIft ? "IndianFamilyTree" : "Social Pulse";

  const load = async () => {
    setLoading(true);
    try {
      if (isIft) {
        // Native IFT articles — a small hand-curated corpus.
        const listRes = await http.get("/articles", { params: { q: q || undefined, tag: activeTag || undefined, limit: 60 } });
        const arr = Array.isArray(listRes.data) ? listRes.data : (listRes.data?.items || []);
        setItems(arr.map((a) => ({
          id:            a.id || a.slug,
          slug:          a.slug,
          title:         a.title,
          excerpt:       a.subtitle || (a.body || "").replace(/[#*>_`\[\]()]/g, "").slice(0, 180),
          feature_image: a.hero_image || a.cover_image || null,
          author:        a.author_name || "IndianFamilyTree Editorial",
          published_at:  a.updated_at || a.created_at,
          tags:          a.tags || [],
          is_featured:   !!a.is_featured,
        })));
        setTotal(arr.length);
        // Aggregate tags from the current page so the filter chips have
        // something to show even though the API doesn't expose a global
        // tag-catalog endpoint yet. Normalise to the shape the chip
        // renderer expects: {tag, label, count}.
        const tagCounts = {};
        for (const a of arr) for (const t of (a.tags || [])) tagCounts[t] = (tagCounts[t] || 0) + 1;
        setTags(
          Object.entries(tagCounts)
            .sort((x, y) => y[1] - x[1])
            .map(([slug, count]) => ({ tag: slug, label: slug, count }))
        );
      } else {
        const [listRes, tagRes] = await Promise.all([
          activeTag
            ? http.get(`/sp-articles/by-tag/${encodeURIComponent(activeTag)}`, { params: { limit: 60 } })
            : http.get("/sp-articles", { params: { q: q || undefined, limit: 60 } }),
          http.get("/sp-articles/tags/list"),
        ]);
        setItems(listRes.data.items);
        setTotal(listRes.data.total);
        setTags(tagRes.data.items);
      }
    } catch (e) {
      toast.error("Failed to load articles");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeTag]);

  const submit = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (q) p.set("q", q); else p.delete("q");
    setActiveTag(""); // free-text search clears any active tag
    p.delete("tag");
    setParams(p, { replace: true });
    load();
  };

  const pickTag = (tag) => {
    const p = new URLSearchParams(params);
    if (activeTag && activeTag.toLowerCase() === tag.toLowerCase()) {
      setActiveTag("");
      p.delete("tag");
    } else {
      setActiveTag(tag);
      p.set("tag", tag);
      p.delete("q");
      setQ("");
    }
    setParams(p, { replace: true });
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await http.post("/sp-articles/sync", { limit: 60, include_sitemap: true });
      toast.success(`Imported ${data.inserted} new · updated ${data.updated}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16" data-testid="articles-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div className="max-w-3xl">
          <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
            <Newspaper size={13} className="inline mr-1.5 -mt-0.5" /> ARTICLES
          </div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight font-black">
            Stories, guides & pulse.
          </h1>
          <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--wofy-text-soft)" }}>
            {isIft
              ? <>Long-form articles from the <b>IndianFamilyTree</b> editorial team — heritage, culture, funeral traditions, family history &amp; genealogy guides. {total > 0 && `${total} article${total === 1 ? "" : "s"} available.`}</>
              : <>Long-form articles from the Social Pulse editorial team — travel, airlines &amp; airports, countries, and community pieces. {total > 0 && `${total} article${total === 1 ? "" : "s"} available.`}</>
            }
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <Link to="/articles/new" data-testid="articles-new" className="wofy-btn-primary">
              <Plus className="w-4 h-4" /> New article
            </Link>
            {!isIft && (
              <button onClick={sync} disabled={syncing} data-testid="articles-sync" className="wofy-btn-ghost">
                <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Syncing…" : "Sync from socialpulse.com"}
              </button>
            )}
          </div>
        )}
      </div>

      <form onSubmit={submit} className="mb-6 flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
          <input
            data-testid="articles-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search articles by title or content…"
            className="w-full rounded-full border pl-10 pr-4 py-3 text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
          />
        </div>
        <button type="submit" className="wofy-btn-primary">Search</button>
      </form>

      {tags.length > 0 && (
        <div className="mb-8 flex flex-wrap gap-2" data-testid="articles-tag-chips">
          <button
            onClick={() => pickTag("")}
            className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-full border"
            style={{
              borderColor: !activeTag ? "var(--wofy-primary)" : "var(--wofy-border)",
              background: !activeTag ? "var(--wofy-primary)" : "transparent",
              color: !activeTag ? "white" : "var(--wofy-text-soft)",
            }}
          >
            All
          </button>
          {tags.map((t) => {
            const on = activeTag && activeTag.toLowerCase() === t.tag.toLowerCase();
            return (
              <button
                key={t.tag}
                onClick={() => pickTag(t.label)}
                data-testid={`articles-tag-${t.tag}`}
                className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-full border"
                style={{
                  borderColor: on ? "var(--wofy-primary)" : "var(--wofy-border)",
                  background: on ? "var(--wofy-primary)" : "transparent",
                  color: on ? "white" : "var(--wofy-text-soft)",
                }}
              >
                {t.label} <span className="opacity-70">({t.count})</span>
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading…</p>
      ) : items.length === 0 ? (
        <div className="wofy-card text-center py-12" style={{ color: "var(--wofy-text-soft)" }}>
          {q || activeTag ? "No articles match your filter." : "No articles yet."}
          {isAdmin && !q && !activeTag && (
            <div className="mt-4">
              <button onClick={sync} className="wofy-btn-primary" disabled={syncing}>
                {syncing ? "Importing…" : "Import from socialpulse.com"}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
          {items.map((a) => <ArticleCard key={a.id} a={a} />)}
        </div>
      )}
    </div>
  );
}

function ArticleCard({ a }) {
  const date = a.published_at ? new Date(a.published_at) : null;
  return (
    <Link
      to={`/articles/${a.slug}`}
      data-testid={`article-card-${a.slug}`}
      className="group block rounded-2xl overflow-hidden border transition-shadow hover:shadow-lg"
      style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-paper)" }}
    >
      {a.feature_image && (
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={a.feature_image}
            alt={a.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          {a.is_featured && (
            <span className="absolute top-3 left-3 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full" style={{ background: "var(--wofy-primary)", color: "white" }}>
              Featured
            </span>
          )}
        </div>
      )}
      <div className="p-5">
        <div className="text-[10px] font-mono uppercase tracking-wider mb-2" style={{ color: "var(--wofy-text-soft)" }}>
          {date ? date.toLocaleDateString(undefined, { dateStyle: "medium" }) : ""}
          {a.author && ` · ${a.author}`}
        </div>
        <h2 className="font-display text-xl leading-snug mb-2">{a.title}</h2>
        {a.excerpt && (
          <p className="text-sm line-clamp-3" style={{ color: "var(--wofy-text-soft)" }}>{a.excerpt}</p>
        )}
        {a.tags && a.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {a.tags.slice(0, 3).map((t) => (
              <span key={t} className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}>{t}</span>
            ))}
          </div>
        )}
        <div className="mt-4 inline-flex items-center gap-1 text-xs font-medium" style={{ color: "var(--wofy-primary)" }}>
          Read article <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      </div>
    </Link>
  );
}

